import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Languages, Mail, Copy, Sparkles, AlertTriangle } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

/**
 * AI Language Studio — Translation + Newsletter drafting.
 * Both endpoints return draft-only output; nothing is auto-published.
 */
const LANGS = [
    { code: "hi", label: "Hindi (हिन्दी)" },
    { code: "mr", label: "Marathi (मराठी)" },
    { code: "en", label: "English" },
];
const TONES = [
    { v: "professional", l: "Professional" },
    { v: "warm", l: "Warm" },
    { v: "patient-friendly", l: "Patient-friendly" },
];

export default function DoctorAILanguage() {
    // Translation state
    const [srcText, setSrcText] = useState("");
    const [targetLang, setTargetLang] = useState("hi");
    const [contentItems, setContentItems] = useState([]);
    const [pickedItemId, setPickedItemId] = useState("");
    const [transDraft, setTransDraft] = useState("");
    const [transBusy, setTransBusy] = useState(false);
    const [transMeta, setTransMeta] = useState(null);

    // Newsletter state
    const [nlTopic, setNlTopic] = useState("");
    const [nlTone, setNlTone] = useState("professional");
    const [nlDraft, setNlDraft] = useState("");
    const [nlBusy, setNlBusy] = useState(false);
    const [nlMeta, setNlMeta] = useState(null);

    useEffect(() => {
        api.get("/me/content").then((r) => setContentItems(r.data.items || [])).catch(() => {});
    }, []);

    function pickFromMyContent(id) {
        setPickedItemId(id);
        const it = contentItems.find((x) => x.id === id);
        if (!it) return;
        const combined = [it.title, it.description].filter(Boolean).join("\n\n");
        setSrcText(combined);
    }

    async function translate() {
        if (!srcText.trim()) { toast.error("Nothing to translate"); return; }
        setTransBusy(true);
        setTransDraft("");
        setTransMeta(null);
        try {
            const { data } = await api.post("/me/ai/translate", {
                text: srcText,
                target_lang: targetLang,
            });
            setTransDraft(data.draft);
            setTransMeta(data);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 429) toast.error("Rate limit — wait a minute and retry.");
            else if (status === 503) toast.error("AI is unavailable right now.");
            else toast.error(errMsg(e, "Translation failed"));
        } finally { setTransBusy(false); }
    }

    async function newsletter() {
        setNlBusy(true);
        setNlDraft("");
        setNlMeta(null);
        try {
            const { data } = await api.post("/me/ai/newsletter", {
                topic: nlTopic || undefined,
                tone: nlTone,
            });
            setNlDraft(data.draft);
            setNlMeta(data);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 429) toast.error("Rate limit — wait a minute and retry.");
            else if (status === 503) toast.error("AI is unavailable right now.");
            else toast.error(errMsg(e, "Newsletter draft failed"));
        } finally { setNlBusy(false); }
    }

    async function copyToClipboard(text, label) {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} copied to clipboard`);
        } catch {
            toast.error("Copy failed — select and copy manually.");
        }
    }

    return (
        <DoctorShell>
            <div className="mb-6" data-testid="ai-language-header">
                <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">AI Studio · Language</div>
                <h1 className="text-2xl font-bold text-[#123B5D]">Translate content · Draft newsletters</h1>
                <p className="text-sm text-[#17212B]/60 mt-1">
                    Every output below is a <strong>draft</strong>. Review carefully before
                    using anywhere public — you own accuracy, the platform never auto-publishes.
                </p>
            </div>

            {/* Translation card */}
            <Card className="border-[#123B5D]/10 mb-6" data-testid="ai-translate-card">
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <Languages size={16} className="text-[#0F766E]" /> AI Translation (Hindi · Marathi · English)
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                        <div className="md:col-span-2">
                            <Label>Source (English)</Label>
                            <Textarea rows={5} value={srcText} onChange={(e) => setSrcText(e.target.value)}
                                placeholder="Paste text, or pick an item from your Content Hub below."
                                data-testid="trans-src" />
                        </div>
                        <div>
                            <Label>Translate to</Label>
                            <select
                                value={targetLang}
                                onChange={(e) => setTargetLang(e.target.value)}
                                className="w-full h-10 px-3 text-sm bg-white border border-[#E5E9F2] rounded-md focus:outline-none focus:border-[#3A80F7]"
                                data-testid="trans-lang"
                            >
                                {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                            </select>
                        </div>
                    </div>
                    {contentItems.length > 0 && (
                        <div>
                            <Label className="text-xs">Or pick from your existing content ({contentItems.length})</Label>
                            <select
                                value={pickedItemId}
                                onChange={(e) => pickFromMyContent(e.target.value)}
                                className="w-full h-10 px-3 text-sm bg-white border border-[#E5E9F2] rounded-md"
                                data-testid="trans-pick"
                            >
                                <option value="">— Select an item —</option>
                                {contentItems.slice(0, 50).map((it) => (
                                    <option key={it.id} value={it.id}>[{it.kind}] {it.title}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <Button onClick={translate} disabled={transBusy} data-testid="trans-run">
                        <Sparkles size={13} /> {transBusy ? "Translating…" : "Translate"}
                    </Button>

                    {transDraft && (
                        <div className="mt-3 border border-[#0F766E]/30 bg-[#0F766E]/5 rounded-md p-3" data-testid="trans-result">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-[10px] font-bold uppercase tracking-wider bg-[#D97706] text-white px-1.5 py-0.5 rounded">Draft</span>
                                <span className="text-xs text-[#17212B]/70 font-semibold">{transMeta?.target_name}</span>
                                <Button size="sm" variant="ghost" onClick={() => copyToClipboard(transDraft, "Translation")} className="ml-auto h-7" data-testid="trans-copy">
                                    <Copy size={12} /> Copy
                                </Button>
                            </div>
                            <div className="whitespace-pre-wrap text-sm text-[#0F172A]" data-testid="trans-draft">{transDraft}</div>
                            {transMeta?.disclaimer && (
                                <div className="mt-3 flex gap-2 text-[11px] text-[#D97706]"><AlertTriangle size={12} className="mt-0.5 shrink-0" /> <span>{transMeta.disclaimer}</span></div>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Newsletter card */}
            <Card className="border-[#123B5D]/10" data-testid="ai-newsletter-card">
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <Mail size={16} className="text-[#0F766E]" /> AI Newsletter drafting
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <Label>Topic focus (optional)</Label>
                            <Input value={nlTopic} onChange={(e) => setNlTopic(e.target.value)}
                                placeholder="e.g. Summer skincare, Vaccination catch-up"
                                data-testid="nl-topic" />
                        </div>
                        <div>
                            <Label>Tone</Label>
                            <select value={nlTone} onChange={(e) => setNlTone(e.target.value)}
                                className="w-full h-10 px-3 text-sm bg-white border border-[#E5E9F2] rounded-md"
                                data-testid="nl-tone">
                                {TONES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                            </select>
                        </div>
                    </div>
                    <p className="text-xs text-[#17212B]/60">
                        The draft is grounded in your existing profile facts + published content
                        items. If the AI lacks data for a section it inserts <span className="font-mono">[Information Required]</span> — fill in yourself.
                    </p>
                    <Button onClick={newsletter} disabled={nlBusy} data-testid="nl-run">
                        <Sparkles size={13} /> {nlBusy ? "Drafting…" : "Draft newsletter"}
                    </Button>

                    {nlDraft && (
                        <div className="mt-3 border border-[#0F766E]/30 bg-[#0F766E]/5 rounded-md p-3" data-testid="nl-result">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-[10px] font-bold uppercase tracking-wider bg-[#D97706] text-white px-1.5 py-0.5 rounded">Draft</span>
                                <span className="text-xs text-[#17212B]/70">{nlMeta?.grounding_items_used} content item(s) used</span>
                                <Button size="sm" variant="ghost" onClick={() => copyToClipboard(nlDraft, "Newsletter")} className="ml-auto h-7" data-testid="nl-copy">
                                    <Copy size={12} /> Copy
                                </Button>
                            </div>
                            <div className="whitespace-pre-wrap text-sm text-[#0F172A]" data-testid="nl-draft">{nlDraft}</div>
                            {nlMeta?.disclaimer && (
                                <div className="mt-3 flex gap-2 text-[11px] text-[#D97706]"><AlertTriangle size={12} className="mt-0.5 shrink-0" /> <span>{nlMeta.disclaimer}</span></div>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>
        </DoctorShell>
    );
}
